import { createContext, type ReactNode, useContext } from 'react';
import { useMe } from '../api/hooks/useMe';
import { getUnlinkedPayload } from '../api/client';
import type { UnlinkedPayload, UserResponse } from '../api/types';

interface PermissionsContextValue {
  user: UserResponse | null;
  permissions: string[];
  roles: string[];
  isLoading: boolean;
  error: Error | null;
  /**
   * The account is authenticated in Keycloak but has no user row in aid. This is
   * a normal state in a realm shared with other apps, not a failure: it is
   * derived from the `/api/me` query, so it clears itself as soon as an admin
   * provisions the account and the query revalidates.
   */
  unlinked: boolean;
  unlinkedPayload: UnlinkedPayload | null;
  /** Re-runs `/api/me`. The not-linked screen uses it as an explicit retry. */
  refetch: () => void;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  user: null,
  permissions: [],
  roles: [],
  isLoading: false,
  error: null,
  unlinked: false,
  unlinkedPayload: null,
  refetch: () => {},
});

export function usePermissions(): PermissionsContextValue {
  return useContext(PermissionsContext);
}

interface PermissionsProviderProps {
  children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { data: user, isLoading, error, refetch } = useMe();

  const activeRoles = user?.roles.filter((role) => role.isActive === true) ?? [];
  const permissions = [
    ...new Set(activeRoles.flatMap((role) => role.permissions.map((p) => p.name))),
  ];

  const roles = activeRoles.map((role) => role.name);

  const unlinkedPayload = getUnlinkedPayload(error);

  return (
    <PermissionsContext.Provider
      value={{
        user: user ?? null,
        permissions,
        roles,
        isLoading,
        error,
        unlinked: unlinkedPayload !== null,
        unlinkedPayload,
        refetch: () => {
          void refetch();
        },
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}
